import React, { useEffect, useState, useContext } from 'react';
import { Tag, Tooltip } from 'antd';
import { AuthContext } from '../../../../config/AuthContext';
import { GetCazaRecompenses, GetCazaUsuarisRecompenses } from '../../../../dao/jutge/CazaRecompensesDao';

interface CazaRecompensesTagsProps {
    id_caza: string | number;
    idConjunt?: string | number;
    ele: any;
    refreshKey?: number;
}

export const CazaRecompensesTags: React.FC<CazaRecompensesTagsProps> = ({ id_caza, idConjunt, ele, refreshKey = 0 }) => {
    const { user } = useContext(AuthContext);
    const isAlumne = user?.tipus === "2";
    const [recs, setRecs] = useState<any[]>([]);
    const [usuaris, setUsuaris] = useState<any[]>([]);

    useEffect(() => {
        let mounted = true;
        GetCazaRecompenses(id_caza, idConjunt).then(res => {
            if (mounted && res && Array.isArray(res)) setRecs(res);
        }).catch(() => { });
        GetCazaUsuarisRecompenses(id_caza, idConjunt).then(res => {
            if (mounted && res && Array.isArray(res)) setUsuaris(res);
        }).catch(() => { });
        return () => { mounted = false; };
    }, [id_caza, idConjunt, refreshKey]);

    // Fallback: si no hay recompensas en jutge.caza_recompensa, mostrar la recompensa histórica de ele
    if (!recs || recs.length === 0) {
        const legacyType = (ele?.reward_type || ele?.rewardType || '').toUpperCase();
        if (!legacyType) return null;
        const apiUrl = process.env.REACT_APP_API_URL;

        if (legacyType === 'JAVAMON' && (ele?.javamon_config_id || ele?.itemId)) {
            const jmId = ele.javamon_config_id || ele.itemId;
            const url = `${apiUrl}:8061/jutge/game/image/javamon/${jmId}`;
            return (
                <Tooltip title={`Javamon específic #${jmId}`}>
                    <Tag style={{ borderRadius: '8px', padding: '0 4px', display: 'inline-flex', alignItems: 'center' }}>
                        <img src={url} alt="javamon" style={{ width: 16, height: 16, objectFit: 'contain', borderRadius: '50%' }} />
                    </Tag>
                </Tooltip>
            );
        }
        if (legacyType === 'JAVAMON_RANDOM') {
            return (
                <Tooltip title="Javamon aleatori">
                    <Tag color="#fff" style={{ borderRadius: '8px', padding: '0 4px', border: '1px solid #d9363e', color: '#000' }}>
                        ⚔️
                    </Tag>
                </Tooltip>
            );
        }
        if (legacyType === 'JAVAMON_SEASON') {
            return (
                <Tooltip title={`Javamon de temporada${ele?.itemId ? ` #${ele.itemId}` : ''}`}>
                    <Tag color="#f9f0ff" style={{ borderRadius: '8px', padding: '0 4px', border: '1px solid #722ed1', color: '#722ed1', fontWeight: 'bold' }}>
                        ❓
                    </Tag>
                </Tooltip>
            );
        }
        if (legacyType === 'PUNTUACION') {
            return (
                <Tooltip title="Puntuació">
                    <Tag color="black" style={{ borderRadius: '8px', padding: '0 4px', border: '1px solid #333', color: 'gold', fontWeight: 'bold' }}>
                        🪙 +{ele?.reward_valor ?? 5}
                    </Tag>
                </Tooltip>
            );
        }
        if (ele?.itemId) {
            let seg = legacyType.toLowerCase();
            if (seg === 'escudo') seg = 'escut';
            else if (seg === 'arco') seg = 'arc';
            else if (seg === 'shop_item') seg = 'shop';
            const url = `${apiUrl}:8061/jutge/game/image/${seg}/${ele.itemId}`;
            return (
                <Tooltip title={`${legacyType} #${ele.itemId}`}>
                    <Tag style={{ borderRadius: '8px', padding: '0 4px', display: 'inline-flex', alignItems: 'center' }}>
                        <img src={url} alt={legacyType} style={{ width: 16, height: 16, objectFit: 'contain' }} />
                    </Tag>
                </Tooltip>
            );
        }
        return (
            <Tooltip title={legacyType}>
                <Tag style={{ borderRadius: '8px', padding: '0 4px' }}>🎁 {legacyType}</Tag>
            </Tooltip>
        );
    }

    const myAttempts = ele?.intentos_restantes !== undefined && ele?.intentos_global
        ? (ele.intentos_global - ele.intentos_restantes)
        : (ele?.intentos || 0);

    const visibleItems = recs.map((r: any) => {
        let isExhausted = false;
        let exhaustionReason = '';
        let whoGotIt = '';

        // Comprobar si está agotada o superada por límites
        const gotItUsers = usuaris.filter(u => {
            try {
                const uRewards = typeof u.rewards_json === 'string' ? JSON.parse(u.rewards_json) : u.rewards_json;
                return uRewards && Array.isArray(uRewards) && uRewards.some((ur: any) => ur.id === r.id);
            } catch (e) {
                return false;
            }
        });

        if (r.limit_quantity && r.limit_quantity > 0 && (r.claimed_count >= r.limit_quantity)) {
            isExhausted = true;
            exhaustionReason = 'Agotada';
            if (gotItUsers.length > 0) {
                whoGotIt = gotItUsers.map(u => u.nom).join(', ');
                exhaustionReason += ` (${whoGotIt})`;
            }
        } else if (r.intentos_min && myAttempts > 0 && myAttempts < r.intentos_min) {
            isExhausted = true;
            exhaustionReason = `Requiere ${r.intentos_min} intentos (Llevas ${myAttempts})`;
        } else if (r.intentos_max && myAttempts > r.intentos_max) {
            isExhausted = true;
            exhaustionReason = `Límite de intentos superado (Máx ${r.intentos_max}, Llevas ${myAttempts})`;
        } else if (r.time_limit && ele?.tiempo_empleado_segundos && ele.tiempo_empleado_segundos > (r.time_limit * 60)) {
            isExhausted = true;
            const elapsedMin = Math.round(ele.tiempo_empleado_segundos / 60);
            exhaustionReason = `Tiempo límite superado (Máx ${r.time_limit} min, has tardado ${elapsedMin} min)`;
        }

        let text = r.tipo === 'JAVAMON_RANDOM'
            ? 'Javamon Aleatorio'
            : (r.tipo === 'JAVAMON_SEASON' ? 'Javamon de Temporada' : r.tipo);
        if (r.valor && r.valor > 1) text += ` x${r.valor}`;

        const hasImage = r.imagen && r.imagen.length > 0;
        let imgSrc = '';
        if (hasImage) {
            imgSrc = r.imagen.startsWith('data:') || r.imagen.startsWith('http') ? r.imagen : `data:image/png;base64,${r.imagen}`;
        } else if (r.id_item && !['PUNTUACION', 'JAVAMON_RANDOM', 'JAVAMON_SEASON'].includes(r.tipo)) {
            const apiUrl = process.env.REACT_APP_API_URL;
            let typeSegment = r.tipo.toLowerCase();
            if (typeSegment === "escudo") typeSegment = "escut";
            if (typeSegment === "arco") typeSegment = "arc";
            if (typeSegment === "shop_item") typeSegment = "shop";
            imgSrc = `${apiUrl}:8061/jutge/game/image/${typeSegment}/${r.id_item}`;
        }

        let content;
        let bgColor = "transparent";
        let borderColor = "gray";

        if (r.tipo === 'PUNTUACION') {
            content = <span style={{ color: 'gold', fontWeight: 'bold' }}>🪙 +{r.valor}</span>;
            bgColor = "black";
            borderColor = "#333";
        } else if (r.tipo === 'JAVAMON_RANDOM') {
            content = <span>⚔️</span>;
            bgColor = "#fff";
            borderColor = "#d9363e";
        } else if (r.tipo === 'JAVAMON_SEASON') {
            content = <span style={{ color: '#722ed1', fontWeight: 'bold' }}>❓</span>;
            bgColor = "#f9f0ff";
            borderColor = "#722ed1";
        } else if (imgSrc) {
            content = <img src={imgSrc} alt={r.tipo} style={{ width: 16, height: 16, objectFit: 'contain', borderRadius: r.tipo.includes('JAVAMON') ? '50%' : '0' }} />;
        } else {
            content = <span>🎁</span>;
        }

        return {
            ...r,
            renderedContent: content,
            tooltipTitle: isExhausted ? exhaustionReason : text,
            isExhausted,
            exhaustionReason,
            bgColor,
            borderColor,
            grupoNum: Number(r.grupo_seleccion) || 0
        };
    }).filter(Boolean);

    // Agrupar recompensas que pertenezcan a un mismo grupo de selección (grupoNum > 0)
    const groupedElements: React.ReactNode[] = [];
    const processedGroupIds = new Set<number>();

    visibleItems.forEach((item: any, idx: number) => {
        if (item.grupoNum > 0) {
            if (processedGroupIds.has(item.grupoNum)) return;
            processedGroupIds.add(item.grupoNum);

            const groupItems = visibleItems.filter((x: any) => x.grupoNum === item.grupoNum);
            const allExhausted = groupItems.every((x: any) => x.isExhausted);
            const groupTooltip = (
                <div>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>🎯 Recompensa unificada (Elige 1 del Grupo {item.grupoNum}):</div>
                    {groupItems.map((gi: any, gIdx: number) => (
                        <div key={gi.id || gIdx}>• {gi.tooltipTitle}</div>
                    ))}
                </div>
            );

            groupedElements.push(
                <Tooltip key={`group-${item.grupoNum}`} title={groupTooltip}>
                    <Tag
                        style={{
                            fontSize: '0.75rem',
                            borderRadius: '8px',
                            padding: '0 6px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            background: '#f0f5ff',
                            border: '1px solid #adc6ff',
                            cursor: 'help',
                            opacity: allExhausted ? 0.4 : 1,
                            filter: allExhausted ? 'grayscale(100%)' : 'none'
                        }}
                    >
                        <span style={{ fontSize: '10px', color: '#1d39c4', fontWeight: 600, marginRight: 2 }}>
                            G{item.grupoNum}:
                        </span>
                        {groupItems.map((gi: any, gIdx: number) => (
                            <span 
                                key={gi.id || gIdx} 
                                style={{ 
                                    display: 'inline-flex', 
                                    alignItems: 'center',
                                    opacity: gi.isExhausted ? 0.35 : 1,
                                    textDecoration: gi.isExhausted ? 'line-through' : 'none'
                                }}
                            >
                                {gi.renderedContent}
                            </span>
                        ))}
                    </Tag>
                </Tooltip>
            );
        } else {
            groupedElements.push(
                <Tooltip key={item.id || idx} title={item.tooltipTitle}>
                    <Tag
                        color={item.bgColor}
                        style={{
                            fontSize: '0.75rem',
                            borderRadius: '8px',
                            padding: '0 4px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: `1px solid ${item.borderColor}`,
                            cursor: 'help',
                            opacity: item.isExhausted ? 0.4 : 1,
                            textDecoration: item.isExhausted ? 'line-through' : 'none',
                            filter: item.isExhausted ? 'grayscale(100%)' : 'none'
                        }}
                    >
                        {item.renderedContent}
                    </Tag>
                </Tooltip>
            );
        }
    });

    return (
        <div style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
            {groupedElements}
        </div>
    );
};
